import _ from "lodash";
import { convertDate } from "@Services/luxonHelper";
import { GroupTotalFormatters } from "slickgrid-react";
import { formatBytes } from "@Services/constants";
import { t } from "@i18n";

const millisecondsToDays = (milliseconds: any): any => {
  const days = milliseconds / (1000 * 60 * 60 * 24);
  return Math.floor(days);
};

type DateValue = number | string | null | undefined;

interface FilesGridExcelRow {
  nextActionDueDate?: DateValue;
  createdDate?: DateValue;
  reviews?: { createdDate?: DateValue; initiationDate?: DateValue };
}

export const getFilesGridColumns = ({
  userAvatar,
  statusInReviewTemplate,
  showProjectID,
  extraColumns,
  currentReviewLevelFormat,
  getReviewStatus,
  reactGridRef,
}: any): any => {
  let mapper: any = {
    fileId: {
      field: "fileId",
      id: "fileId",
      headerText: t("File ID"),
      name: t("File ID"),
      width: 100,

      originalWidth: 100,
      textAlign: "Left",
      _valueGetter: (params: any): any => {
        if (params.file?.space?.project?.projectId) {
          return `${params.file?.space?.project?.projectId}-${params.file.indexAtProjectLevel}`;
        }
        return "";
      },
      filterType: "Text",
    },
    fileName: {
      field: "fileName",
      id: "fileName",
      width: 300,

      originalWidth: 300,
      textAlign: "Left",
      wrapText: true,
      _valueGetter: (params: any): any => params.fileName,
      headerText: t("File Name"),
      name: t("File Name"),
      filterType: "Text",
    },
    fileType: {
      field: "fileType",
      id: "fileType",
      headerText: t("File Type"),
      name: t("File Type"),

      width: 150,
      _valueGetter: (params: any): any => params.fileType,
      originalWidth: 150,
      textAlign: "Left",
      filterType: "Text",
    },
    fileSize: {
      field: "size",
      id: "fileSize",
      headerText: t("File Size"),
      name: t("File Size"),
      width: 100,

      originalWidth: 100,
      textAlign: "Left",
      _valueGetter: (params: any): any => {
        if (params.size) {
          const size = formatBytes(params.size);
          return size;
        }
        return "";
      },
      _filterValueGetter: (params: any): any => params.size || 0,
      groupTotalsFormatter: GroupTotalFormatters.sumTotals,
      filterType: "FileSize",
    },
    Version: {
      field: "fileVersionNumber",
      id: "Version",
      headerText: t("Version"),
      name: t("Version"),
      width: 100,

      originalWidth: 100,
      textAlign: "Left",
      _valueGetter: (params: any): any => {
        if (params.fileVersionNumber) {
          return `V${params.fileVersionNumber}`;
        }
        return "";
      },
      filterType: "Text",
    },
    Revision: {
      field: "Revision",
      id: "Revision",
      headerText: t("Revision"),
      name: t("Revision"),
      width: 100,

      originalWidth: 100,
      initiallyHidden: true,
      textAlign: "Left",
      _valueGetter: (params: any): any => params?.revisionSeries?.label || "-",
      filterType: "Text",
    },
    durationAtCurrentLevel: {
      field: "durationAtCurrentLevel",
      id: "durationAtCurrentLevel",
      headerText: t("Duration at Current Level"),
      name: t("Duration at Current Level"),
      width: 175,
      originalWidth: 175,
      textAlign: "Left",

      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (params: any): any => {
        const { currentReviewLevel, reviews } = params;
        if (reviews.reviewStatus == "Open") {
          if (currentReviewLevel) {
            const calculatedDuration = Date.now() - currentReviewLevel.finishTime;
            const days = millisecondsToDays(calculatedDuration);
            return `${currentReviewLevel.finishTime ? days : 0} days`;
          }
        }
        return "-";
      },
    },
    totalDuration: {
      field: "totalDuration",
      id: "totalDuration",
      headerText: t("Total Duration"),
      name: t("Total Duration"),
      width: 175,
      originalWidth: 175,
      textAlign: "Left",

      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (params: any): any => {
        const { reviewStatus, initiationDate, finishedDate } = params.reviews;
        let duration;
        if (reviewStatus == "Closed") {
          duration = finishedDate - initiationDate;
        } else if (reviewStatus == "Revoked") {
          duration = finishedDate - initiationDate;
        } else if (reviewStatus == "Open") {
          duration = Date.now() - initiationDate;
        }
        if (duration) {
          const days = millisecondsToDays(duration);
          return Math.max(days, 0);
        }
        return null;
      },
      formatter: (data: number, params: number, a: any, b: unknown, value: any): any => {
        const { reviewStatus, initiationDate, finishedDate } = value.reviews;
        let duration;
        if (reviewStatus == "Closed") {
          duration = finishedDate - initiationDate;
        } else if (reviewStatus == "Revoked") {
          duration = finishedDate - initiationDate;
        } else if (reviewStatus == "Open") {
          duration = Date.now() - initiationDate;
        }
        if (duration) {
          const days = millisecondsToDays(duration);
          return `${Math.max(days, 0)} days`;
        }
        return "-";
      },
      filterType: "Text",
    },
    dueDateOfLevelReview: {
      field: "dueDateOfLevelReview",
      id: "dueDateOfLevelReview",
      headerText: t("Due Date of Current Level"),
      name: t("Due Date of Current Level"),
      width: 175,

      originalWidth: 175,

      textAlign: "Left",
      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (params: any): any => {
        if (params.nextActionDueDate === "" || params.nextActionDueDate == null) return null;
        return Number(params.nextActionDueDate);
      },
      formatter: (_row: number, _cell: number, _value: any, _columnDef: unknown, dataContext: any): any => {
        if (dataContext.nextActionDueDate === "" || dataContext.nextActionDueDate == null) return "-";
        return convertDate(Number(dataContext.nextActionDueDate), "dateForTooltip");
      },
      _excelValueGetter: (params: FilesGridExcelRow) =>
        params.nextActionDueDate != null && params.nextActionDueDate !== ""
          ? convertDate(Number(params.nextActionDueDate), "dateForTooltip")
          : "-",
      filterType: "Date",
    },
    reviewCreatedDate: {
      field: "reviewCreatedDate",
      id: "reviewCreatedDate",
      headerText: t("Review Created On"),
      name: t("Review Created On"),
      width: 175,
      originalWidth: 175,

      textAlign: "Left",
      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (args: any): any => {
        if (!args.reviews?.createdDate) return null;
        return Number(args.reviews.createdDate);
      },
      formatter: (_row: number, _cell: number, _value: any, _columnDef: unknown, dataContext: any): any => {
        if (!dataContext.reviews?.createdDate) return "";
        return convertDate(Number(dataContext.reviews.createdDate), "dateForTooltip");
      },
      _excelValueGetter: (params: FilesGridExcelRow) =>
        params.reviews?.createdDate ? convertDate(Number(params.reviews.createdDate), "dateForTooltip") : "",
      filterType: "Date",
    },
    reviewInitiatedDate: {
      field: "reviewInitiatedDate",
      id: "reviewInitiatedDate",
      headerText: t("Review Initiated On"),
      name: t("Review Initiated On"),
      width: 175,
      originalWidth: 175,

      textAlign: "Left",
      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (args: any): any => {
        if (!args.reviews?.initiationDate) return null;
        return Number(args.reviews.initiationDate);
      },
      formatter: (_row: number, _cell: number, _value: any, _columnDef: unknown, dataContext: any): any => {
        if (!dataContext.reviews?.initiationDate) return "";
        return convertDate(Number(dataContext.reviews.initiationDate), "dateForTooltip");
      },
      _excelValueGetter: (params: FilesGridExcelRow) =>
        params.reviews?.initiationDate ? convertDate(Number(params.reviews.initiationDate), "dateForTooltip") : "",
      filterType: "Date",
    },
    statusInReviews: {
      field: "statusInReviews",
      id: "statusInReviews",
      headerText: t("Status"),
      name: t("Status"),
      width: 170,
      originalWidth: 170,
      textAlign: "Left",
      _valueGetter: (args: any): any => args.reviews.status?.name || "",
      formatter: (data: number, params: number, a: any, b: unknown, value: any): any => statusInReviewTemplate(value),
      filterType: "Text",
    },
    serialId: {
      field: "serialId",
      id: "serialId",
      headerText: t("Review ID"),
      name: t("Review ID"),
      width: 100,

      originalWidth: 100,

      textAlign: "Left",
      _valueGetter: (args: any): any => {
        const { serialId } = args;
        if ([undefined, null].includes(serialId)) {
          return "";
        }
        return `RE - ${serialId}`;
      },
      isReviewColumnType: true,
      filterType: "Text",
    },
    reviewName: {
      field: "reviewName",
      id: "reviewName",
      headerText: t("Review Name"),
      name: t("Review Name"),
      width: 150,
      wrapText: true,

      originalWidth: 150,
      textAlign: "Left",
      _valueGetter: (args: any): any => args.reviewName || "",
      isReviewColumnType: true,
      filterType: "Text",
    },
    reviewFlowName: {
      field: "reviewFlowName",
      id: "reviewFlowName",
      headerText: t("Review Flow"),
      name: t("Review Flow"),
      width: 150,
      wrapText: true,

      originalWidth: 150,
      textAlign: "Left",
      isReviewColumnType: true,
      _valueGetter: (args: any): any => {
        if (args.__group) {
          return "";
        }
        return args.reviewFlow || "";
      },
      filterType: "Text",
    },
    currentReviewLevelSerial: {
      field: "currentReviewLevelSerial",
      id: "currentReviewLevelSerial",
      headerText: t("Current Level ID"),
      name: t("Current Level ID"),
      width: 150,

      originalWidth: 150,
      textAlign: "Left",
      isReviewColumnType: true,
      _valueGetter: (args: any): any => {
        const { currentReviewLevel } = args;
        if ([undefined, null].includes(currentReviewLevel)) {
          return "-";
        }
        return `Level ${currentReviewLevel.serial}`;
      },
      filterType: "Text",
    },
    currentReviewLevelName: {
      field: "currentReviewLevelName",
      id: "currentReviewLevelName",
      headerText: t("Current Level Name"),
      name: t("Current Level Name"),
      width: 175,
      originalWidth: 175,

      textAlign: "Left",
      isReviewColumnType: true,
      formatter: (data: number, params: number, a: any, b: unknown, value: any): any => currentReviewLevelFormat(value),
      _valueGetter: (args: any): any => getReviewStatus(args),
      filterType: "Text",
    },
    spaceName: {
      field: "file.space.spaceName",
      id: "spaceName",
      headerText: t("Space"),

      name: t("Space"),
      width: 120,
      wrapText: true,
      originalWidth: 120,
      _valueGetter: (args: any): any => _.get(args, "file.space.spaceName"),
      textAlign: "Left",
      filterType: "Text",
    },
    fullName: {
      field: "createdBy.fullName",
      id: "fullName",
      headerText: t("Created by"),
      name: t("Created by"),
      width: 200,
      originalWidth: 200,
      textAlign: "Left",
      _valueGetter: (args: any): any => _.get(args, "createdBy.fullName") || "",
      formatter: (data: number, params: number, a: any, b: unknown, value: any): any => userAvatar(value),
      filterType: "Text",
    },
    createdDate: {
      field: "createdDate",
      id: "createdDate",
      headerText: t("Created On"),
      name: t("Created On"),
      _valueGetter: (params: any): any => {
        if (!params.createdDate) return null;
        return Number(params.createdDate);
      },
      formatter: (_row: number, _cell: number, _value: any, _columnDef: unknown, dataContext: any): any => {
        if (!dataContext.createdDate) return "";
        return convertDate(Number(dataContext.createdDate), "dateForTooltip");
      },
      _excelValueGetter: (params: FilesGridExcelRow) =>
        params.createdDate ? convertDate(Number(params.createdDate), "dateForTooltip") : "",

      width: 200,
      originalWidth: 200,
      textAlign: "Left",
      filterType: "Date",
    },
    conversionStatus: {
      field: "conversionStatus",
      id: "conversionStatus",
      headerText: t("Conversion Status"),
      name: t("Conversion Status"),
      width: 120,
      originalWidth: 120,
      textAlign: "Left",

      initiallyHidden: true,
      _valueGetter: (params: any): any => {
        const mapper: any = {
          NOT_STARTED: "Not Started",
          RETRY: "Retry",
          IN_PROGRESS: "In Progress",
          COMPLETED: "Completed",
          ERRORED: "Errored",
          NOT_REQUIRED: "",
        };
        if (Object.hasOwn(mapper, params.conversionStatus)) {
          return mapper[params.conversionStatus];
        }
        return "";
      },
      filterType: "Text",
    },
  };

  if (showProjectID) {
    mapper.projectId = {
      field: "file.space.project.projectId",
      id: "projectId",
      headerText: t("Project ID"),
      name: t("Project ID"),
      width: 80,

      originalWidth: 80,
      _valueGetter: (args: any): any => _.get(args, "file.space.project.projectId"),
      textAlign: "Left",
    };
  }
  mapper = { ...mapper, ...extraColumns };

  return Object.values(mapper).map((data: any): any => ({
    filterable: true,
    sortable: true,
    groupable: true,
    type: "string",
    filter: {
      filterPredicate: (columnData: any, searchData: any): any => {
        if (data._valueGetter && ![undefined, null].includes(data._valueGetter(columnData))) {
          return data
            ._valueGetter(columnData)
            .toString()
            .toLowerCase()
            .includes(searchData.parsedSearchTerms[0].toLowerCase());
        }
      },
    },
    excelExportOptions: {
      valueParserCallback: (dataObj: any, { dataContext }: any): any =>
        (data._valueGetter ? data._valueGetter(dataContext) : "") || "",
    },
    grouping: {
      getter: (args: any): any => (data._valueGetter ? data._valueGetter(args) : "") || "",
      collapsed: true,
      formatter(g: any): any {
        const visibleColumnsValues: any = new Set();
        reactGridRef.current.slickGrid.columns.map((data: any) => {
          visibleColumnsValues[data.id] = new Set();
        });

        for (const obj of g.rows) {
          for (const column of reactGridRef.current.slickGrid.columns) {
            if (column._valueGetter) {
              visibleColumnsValues[column.id].add(column._valueGetter(obj));
            }
          }
        }

        let html = "";
        reactGridRef.current.slickGrid.columns.map((data: any, index: number) => {
          if (visibleColumnsValues[data.id].size == 1 && Array.from(visibleColumnsValues[data.id])[0]) {
            html += `<span class='slick-cell l${index} r${index}'>${
              Array.from(visibleColumnsValues[data.id])[0]
            }</span>`;
          } else if (visibleColumnsValues[data.id].size > 1) {
            html += `<span class='slick-cell l${index} r${index} italic'>varies</span>`;
          }
        });
        return html;
      },
    },
    ...data,
  }));
};

export const getFilesGridToolbar = ({ searchTemplate, excelExportTemplate }: any): any => [
  {
    id: "excel_export",
    align: "Left" as const,
    template: excelExportTemplate,
  },
  {
    id: "search",
    align: "Right" as const,
    template: searchTemplate,
  },
];
